import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
    UsersIcon,
    CubeIcon,
    CreditCardIcon,
    DocumentReportIcon,
    SupportIcon,
    CogIcon,
    LogoutIcon,
    BookOpenIcon,
    DocumentAddIcon,
    UserGroupIcon,
    PlusIcon,
    UploadIcon,
    BellIcon,
    SearchCircleIcon,
} from "@heroicons/react/solid";

import { logout } from "../../../utils/api";
import { BUTTON_VARIANT_TYPES, NO_ACCESS_TYPES, PERMISSIONS } from "../../../utils/constants";
import { DashboardContext } from "../../../context/DashboardContext";

import Link from "./Link";
import Button from "../../SharedComponents/Button";
import CollapsibleLinks from "./CollapsibleLinks";
import AccessControl from "../../SharedComponents/AccessControl";

function Menu() {
    const context = useContext(DashboardContext);

    const navigate = useNavigate();

    const handleSuccessfulLogout = () => {
        context.setLoggedIn(false);
        navigate("/login");
    };

    const handleLogout = async () => {
        try {
            await logout();
            handleSuccessfulLogout();
        } catch (alert) {
            console.log(alert.message);
        }
    };

    return (
        <div className={"flex-none grid grid-flow-row lg:grid hidden h-full z-30"} style={{ width: "260px" }}>
            <div className={"grid grid-flow-row md:grid hidden z-10 shadow-sm bg-white rounded-lg h-full"}>
                <div className="grid grid-flow-row content-start bg-white rounded-lg transition transition-height">
                    <div className="py-6 pl-6 flex items-center text-indigo-900 w-24">
                        <img alt="logo" className="w-20" src={context.getImage("shortLogo")} />
                    </div>
                    <div
                        className={
                            "py-5 px-6 flex items-center justify-between text-indigo-900 bg-gradient-to-r " +
                            "from-secondary-50"
                        }
                    >
                        <div className={"flex flex-col"}>
                            <span className="font-medium">{context.userDetails.name}</span>
                            <span className="font-light text-sm mt-1 text-ellipsis overflow-hidden w-48">
                                {context.userDetails.email}
                            </span>
                        </div>
                    </div>
                    <AccessControl
                        requiredPermission={PERMISSIONS.ACCESS_APPLICATIONS}
                        noAccessType={NO_ACCESS_TYPES.BLANK}
                    >
                        <Link name="Apps" href={"/apps"} icon={<CubeIcon className="block h-4 w-4 mr-2" />} />
                    </AccessControl>
                    <AccessControl
                        requiredPermission={PERMISSIONS.ACCESS_ASSESSMENTS}
                        noAccessType={NO_ACCESS_TYPES.BLANK}
                    >
                        <CollapsibleLinks childLinkCount={1}>
                            <Link
                                href={"/assessments"}
                                name="Assessments"
                                icon={<BookOpenIcon className="block h-4 w-4 mr-2" />}
                                parent
                            />
                            <AccessControl
                                requiredPermission={PERMISSIONS.ASSIGN_ASSESSMENTS}
                                noAccessType={NO_ACCESS_TYPES.BLANK}
                                href={"/assessments/assign-assessment"}
                            >
                                <Link
                                    name="Assign Assessment"
                                    href={"/assessments/assign-assessment"}
                                    icon={<DocumentAddIcon className="block h-4 w-4 mr-2" />}
                                    extraStyling={"ml-6"}
                                />
                            </AccessControl>
                        </CollapsibleLinks>
                    </AccessControl>

                    {context.userPermissions.includes(PERMISSIONS.ACCESS_SETUP_USER_PAGE) ? (
                        <AccessControl
                            requiredPermission={PERMISSIONS.VIEW_USERS_LIST}
                            noAccessType={NO_ACCESS_TYPES.BLANK}
                        >
                            <CollapsibleLinks childLinkCount={1}>
                                <Link
                                    name="Users"
                                    href={"/users"}
                                    icon={<UsersIcon className="block h-4 w-4 mr-2" />}
                                    parent
                                />
                                <AccessControl
                                    requiredPermission={PERMISSIONS.CREATE_USER_ACCOUNTS}
                                    noAccessType={NO_ACCESS_TYPES.BLANK}
                                    href={"/users/add-user"}
                                >
                                    <Link
                                        name="Add User"
                                        href={"/users/add-user"}
                                        icon={<PlusIcon className="block h-4 w-4 mr-2" />}
                                        extraStyling={"ml-6"}
                                    />
                                </AccessControl>
                            </CollapsibleLinks>
                        </AccessControl>
                    ) : (
                        <AccessControl
                            requiredPermission={PERMISSIONS.VIEW_USERS_LIST}
                            noAccessType={NO_ACCESS_TYPES.BLANK}
                        >
                            <Link
                                name="Users"
                                href={"/users"}
                                icon={<UsersIcon className="block h-4 w-4 mr-2" />}
                                parent
                            />
                        </AccessControl>
                    )}

                    <AccessControl
                        requiredPermission={PERMISSIONS.VIEW_USER_GROUPS_LIST}
                        noAccessType={NO_ACCESS_TYPES.BLANK}
                    >
                        <CollapsibleLinks childLinkCount={1}>
                            <Link
                                name="User Groups"
                                href={"/user-groups"}
                                icon={<UserGroupIcon className="block h-4 w-4 mr-2" />}
                                parent
                            />
                            <AccessControl
                                requiredPermission={PERMISSIONS.CREATE_USER_GROUPS}
                                noAccessType={NO_ACCESS_TYPES.BLANK}
                                href={"/user-groups/add-user-group"}
                            >
                                <Link
                                    name="Add User Group"
                                    href={"/user-groups/add-user-group"}
                                    icon={<PlusIcon className="block h-4 w-4 mr-2" />}
                                    extraStyling={"ml-6"}
                                />
                            </AccessControl>
                        </CollapsibleLinks>
                    </AccessControl>
                    <AccessControl
                        requiredPermission={[PERMISSIONS.VIEW_REPORTS, PERMISSIONS.VIEW_REPORTS_TAB]}
                        noAccessType={NO_ACCESS_TYPES.BLANK}
                    >
                        <Link
                            name="Reports"
                            href={"/reports"}
                            icon={<DocumentReportIcon className="block h-4 w-4 mr-2" />}
                            parent
                        />
                    </AccessControl>
                    <AccessControl requiredPermission={PERMISSIONS.UPLOAD_REPORTS} noAccessType={NO_ACCESS_TYPES.BLANK}>
                        <Link
                            name="Upload Report"
                            href={"/reports/upload-report"}
                            icon={<UploadIcon className="block h-4 w-4 mr-2" />}
                            parent
                        />
                    </AccessControl>
                    {process.env.REACT_APP_FEATURE_ENABLEMENT_LEVEL <= 0 && (
                        <Link
                            name="Billing"
                            href={"/billing"}
                            icon={<CreditCardIcon className="block h-4 w-4 mr-2" />}
                        />
                    )}
                </div>
                <div className="grid grid-flow-row content-end bg-white rounded-lg mt-12">
                    {(process.env.REACT_APP_ENV === "DEMO" || process.env.REACT_APP_ENV === "NPD") && (
                        <Link
                            name="Audit Log"
                            href={"/audit-log"}
                            icon={<SearchCircleIcon className="block h-4 w-4 mr-2" />}
                        />
                    )}
                    {process.env.REACT_APP_FEATURE_ENABLEMENT_LEVEL <= 0 && (
                        <Link
                            name="Notifications"
                            href={"/notifications"}
                            icon={<BellIcon className="block h-4 w-4 mr-2" />}
                        />
                    )}

                    <AccessControl
                        requiredPermission={PERMISSIONS.ACCESS_HELP_AND_SUPPORT}
                        noAccessType={NO_ACCESS_TYPES.BLANK}
                        href={"/help-and-support"}
                    >
                        <Link
                            name="Help & support"
                            href={"/help-and-support"}
                            icon={<SupportIcon className="block h-4 w-4 mr-2" />}
                        />
                    </AccessControl>
                    <Link name="Settings" href={"/settings"} icon={<CogIcon className="block h-4 w-4 mr-2" />} />
                    <div className="p-6 flex items-center">
                        <Button
                            colour={BUTTON_VARIANT_TYPES.GRAY}
                            text="Logout"
                            icon={<LogoutIcon className="block h-4 w-4 ml-2" />}
                            extraStyling={"w-full justify-center"}
                            onClick={handleLogout}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Menu;
